// src/components/chat/LicenceExpiryBanner.tsx

import * as React from "react";
import { Megaphone } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useChatSettings } from "../../contexts/ChatSettingsContext";
import { useTenantSelectorAccess } from "../../hooks/useTenantSelectorAccess";
import { extractTenantId } from "../../utils/jwtUtils";
import { LICENCE_NOTICES, resolveLicenceNotice } from "../../config/licenceNotice";

/**
 * Permanent notice that a tenant's licence is running out, shown at the very top
 * of the task pane above every other banner.
 *
 * It has no dismiss control and no buttons, both on purpose. It cannot be closed
 * because a licence ending is not something a user should be able to mute; it
 * offers no action because the add-in doesn't know who the customer's admin is,
 * so any button would either fail or open an empty mail client. It states the
 * fact and names who to talk to.
 *
 * The surface stays amber at every stage and only the progress tick turns red in
 * the final week. Red, amber and green carry contract-risk meaning everywhere
 * else in this product, so a red banner would read as a finding about the
 * document rather than a billing notice.
 */

// Days of runway the tick represents. At 30 days out it is empty; on the last
// day it is full. Purely visual - the copy is driven by the real day count.
const TICK_WINDOW_DAYS = 30;

// The tick turns red at this many days left or fewer.
const RED_THRESHOLD_DAYS = 7;

const AMBER_BG = "#fff4ce";
const AMBER_BORDER = "#ffe7a0";
const INK = "#5c4a12";
const TRACK = "rgba(92, 74, 18, 0.14)";
const TICK_AMBER = "#d99a10";
const TICK_RED = "#e74c3c";

// Segoe UI's semibold weight is 590 rather than the usual 600. Where the
// variable font is available, asking for 600 lands heavier than the rest of the
// add-in; elsewhere both resolve to the same face, so 590 costs nothing.
const SEMIBOLD = 590;

// The icon column: a 16px icon plus the 9px gap after it. The tick is indented
// by this much so it starts under the text rather than under the icon.
const ICON_LANE = 25;

function msUntilNextLocalMidnight(now: Date): number {
  const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

  return nextMidnight.getTime() - now.getTime();
}

/** "2 Oct" in the user's locale. */
function formatShortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * The message, with the part that carries the deadline emphasised.
 *
 * Every variant gives both the relative count and the absolute date: the count
 * is what creates urgency, the date is what the user quotes to procurement.
 */
function renderMessage(daysLeft: number, endDate: Date): React.ReactNode {
  const bold = (text: string) => <strong style={{ fontWeight: SEMIBOLD }}>{text}</strong>;
  const contactAdmin =
    " Please contact your admin or procurement team to keep your licence active.";
  const shortDate = formatShortDate(endDate);

  if (daysLeft === 0) {
    return (
      <>
        {bold("Today is the last day of your Contract Space access.")}
        {contactAdmin}
      </>
    );
  }

  if (daysLeft === 1) {
    return (
      <>
        Your Contract Space access ends {bold(`tomorrow, ${shortDate}`)}.{contactAdmin}
      </>
    );
  }

  return (
    <>
      {bold(`Your Contract Space access ends in ${daysLeft} days (${shortDate}).`)}
      {contactAdmin}
    </>
  );
}

const LicenceExpiryBanner: React.FC = () => {
  const { accessToken } = useAuth();
  const { getTenantId } = useChatSettings();
  const canPreviewTenants = useTenantSelectorAccess();

  // The user's real tenant, from the token AuthContext holds. That token is
  // never the tenant-selector-rewritten copy (the rewrite is applied to a
  // request-local clone), so a GP user can never make this banner disappear.
  // Memoised because decoding the token means a base64 and a JSON parse, and
  // this component re-renders on every streamed chunk.
  const homeTenantId = React.useMemo(
    () => (accessToken ? extractTenantId(accessToken) : null),
    [accessToken]
  );

  // Internal preview: someone allowed to use the tenant selector sees the banner
  // of the tenant they have selected, which is the only way to check this before
  // the date arrives without a customer account. Gated on the same access hook
  // the chat send path uses, so a selection can only ever apply to someone who
  // is allowed to make it - the same rule the tid rewrite itself follows.
  const previewTenantId = canPreviewTenants ? getTenantId() : "";

  // Deliberately computed during render rather than held in state: the task
  // pane's webview suspends timers while it is hidden (observed on Word Desktop
  // Windows, see AuthContext), so a stored value can go stale. Reading the clock
  // here means any re-render of the pane also refreshes the count, the same
  // recompute-at-point-of-use approach the token refresh uses.
  const notice = resolveLicenceNotice(LICENCE_NOTICES, previewTenantId || homeTenantId, new Date());

  // Nudge a re-render at the next local midnight, the only moment the number can
  // change. If the webview suspends the timer while the pane is hidden, nobody
  // is reading a stale count anyway, and the next send or agent switch re-renders
  // the pane. A pane left visible but untouched across midnight is what this
  // covers.
  const [midnightTick, setMidnightTick] = React.useState(0);
  // Depends on the day count rather than on `notice`, which is a new object on
  // every render - this component re-renders on every streamed chunk, and that
  // would reschedule the timer each time.
  const daysLeftForTimer = notice?.daysLeft;

  React.useEffect(() => {
    if (daysLeftForTimer === undefined) return undefined;
    // The extra second lands us just past midnight rather than exactly on it.
    const timer = setTimeout(
      () => setMidnightTick((count) => count + 1),
      msUntilNextLocalMidnight(new Date()) + 1000
    );

    return () => clearTimeout(timer);
  }, [daysLeftForTimer, midnightTick]);

  if (!notice) return null;

  const { daysLeft, endDate } = notice;
  const elapsed = (TICK_WINDOW_DAYS - daysLeft) / TICK_WINDOW_DAYS;
  const tickPercent = Math.min(100, Math.max(0, elapsed * 100));
  const tickColor = daysLeft <= RED_THRESHOLD_DAYS ? TICK_RED : TICK_AMBER;

  return (
    <div
      role="status"
      style={{
        // Full-bleed band: the negative horizontal margin cancels the pane's 8px
        // padding so the banner meets both edges and reads as part of the window
        // chrome rather than as a card floating in the conversation. No gap
        // underneath - the border already separates it, and the pane is short
        // enough that a permanent banner should cost as few rows as it can.
        margin: "-8px -8px 0",
        boxSizing: "border-box",
        // Tighter at the bottom than the top: the tick only needs enough room to
        // not look clipped against the border.
        padding: "10px 12px 8px",
        backgroundColor: AMBER_BG,
        borderBottom: `1px solid ${AMBER_BORDER}`,
        color: INK,
        fontFamily:
          "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
        fontSize: "13px",
        lineHeight: 1.45,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "9px" }}>
        <Megaphone
          size={16}
          strokeWidth={1.9}
          aria-hidden="true"
          style={{ flex: "0 0 auto", marginTop: "1px" }}
        />
        <div style={{ flex: 1 }}>{renderMessage(daysLeft, endDate)}</div>
      </div>

      <div
        style={{
          marginLeft: `${ICON_LANE}px`,
          width: `calc(100% - ${ICON_LANE}px)`,
          marginTop: "9px",
          height: "3px",
          backgroundColor: TRACK,
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${tickPercent}%`,
            backgroundColor: tickColor,
            borderRadius: "2px",
          }}
        />
      </div>
    </div>
  );
};

export default LicenceExpiryBanner;
